import React from 'react';
import { Head } from 'vite-react-ssg';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogImageAbsolute?: string;
  structuredData?: object | object[];
  isArticle?: boolean;
  noindex?: boolean;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title = "Milky Way Planner — Free Astrophotography Planning Tool | Galactic Core Calendar",
  description = "Plan your Milky Way photography with our free visibility calendar. See galactic core rise/set times, moon phases, and nightly ratings 1–10 for any location worldwide.",
  keywords = "milky way calendar, milky way planner, astrophotography planner, milky way visibility, galactic core visibility, milky way season, moon phase calendar, night sky planner, dark sky locations, astrophotography calendar, milky way photography, best time to see milky way, galactic core rise time",
  canonicalUrl,
  ogImage = "/images/og-default.png",
  ogImageAbsolute,
  structuredData,
  isArticle = false,
  noindex = false
}) => {
  const baseUrl = "https://www.milkywayplanner.com";
  // A page that passes no canonical gets its own route path. Falling back to
  // the homepage made /workshops and /workshops/instructors duplicates of /
  // (ERI-2765).
  const { pathname } = useLocation();
  const ownPath = pathname.replace(/\/+$/, '');
  const fullUrl = canonicalUrl ? `${baseUrl}${canonicalUrl}` : `${baseUrl}${ownPath}`;
  const finalOgImage = ogImageAbsolute || `${baseUrl}${ogImage}`;
  
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Milky Way Planner",
    "description": description,
    "url": baseUrl,
    "applicationCategory": "PhotographyApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "creator": {
      "@type": "Organization",
      "name": "Milky Way Planner",
      "url": baseUrl
    },
    "featureList": [
      "Galactic core timing calculations",
      "Moon phase analysis",
      "Worldwide location support",
      "Visibility ratings",
      "Astronomical data integration"
    ]
  };

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <meta name="author" content="Milky Way Planner" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Open Graph tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={isArticle ? "article" : "website"} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Milky Way Planner" />
      
      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@MilkyWayPlan" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={finalOgImage} />
      
      {/* Structured Data */}
      {Array.isArray(structuredData) ? (
        structuredData.map((data, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(data)}
          </script>
        ))
      ) : (
        <script type="application/ld+json">
          {JSON.stringify(structuredData || defaultStructuredData)}
        </script>
      )}
    </Head>
  );
};
